import { isAxiosError } from "axios";
import { format } from "date-fns";
import { Button, Modal } from "flowbite-react";
import Papa from "papaparse";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DateTimePicker from "../components/DateTimePicker";
import { EmailPreview } from "../components/EmailPreview";
import { TextInput } from "../components/form";
import { Layout } from "../components/Layout";
import StatusIcon from "../components/StatusIcon";
import { EmailTemplate, Notification } from "../models/notification";
import { UserInfo } from "../models/user-info";
import notificationService from "../services/notification-service";
import { Toast } from "../services/toast-service";

type PendingDetailScreenProps = {
  userInfo: UserInfo;
};

const PendingDetailScreen: React.FC<PendingDetailScreenProps> = (props) => {
  const params = useParams();

  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<Notification>();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [sendAt, setSendAt] = useState("");
  const [status, setStatus] = useState("");
  const [recipientsAll, setRecipientsAll] = useState(false);
  const [count, setCount] = useState(0);
  const [recipientsCSVFile, setRecipientsCSVFile] = useState<File | undefined>(
    undefined,
  );
  const [emailTemplate, setEmailTemplate] = useState<EmailTemplate | undefined>(
    undefined,
  );
  const [emailTemplateVariables, setEmailTemplateVariables] = useState<any>({});
  const [emailTemplateVariablesMap, setEmailTemplateVariablesMap] =
    useState<Map<string, string>>();
  const [csvError, setCsvError] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();

  const init = async () => {
    if (params.uuid) {
      setLoading(true);
      const item = await notificationService.getNotification(params.uuid);
      setNotification(item);
      setTitle(item.Title);
      setBody(item.Body);
      setLink(item.Link);
      const sendAtformatted = format(new Date(item.SendAt), "yyyy-MM-dd HH:mm");
      setSendAt(sendAtformatted);
      setStatus(item.Status);
      setRecipientsAll(item.RecipientsAll);
      if (item.EmailTemplateVariables) {
        setEmailTemplateVariables(item.EmailTemplateVariables);
        setEmailTemplateVariablesMap(
          new Map(Object.entries(item.EmailTemplateVariables)),
        );
      } else {
        setEmailTemplateVariables(undefined);
        setEmailTemplateVariablesMap(undefined);
      }
      if (item.EmailTemplate) {
        getEmailTemplate(item.EmailTemplate);
      }
      if (item.Status === "pending") {
        const count = await notificationService.getRecipientNotificationCount(
          params.uuid,
        );
        setCount(count);
      } else if (item.Status === "sent") {
        const count = await notificationService.getNotificationUserCount(
          params.uuid,
        );
        setCount(count);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    init();
  }, [params.uuid]);

  const onUpdate = async (approved: boolean) => {
    if (!loading) {
      setLoading(true);
      try {
        if (!params.uuid) {
          throw new Error("No id for notification provided.");
        }
        await notificationService.approveNotification(params.uuid, approved);
        Toast(
          "Success!!",
          `The notification has been ${approved ? "approved" : "rejected"}.`,
          "success",
        );
        navigate("/approvals");
      } catch (err) {
        console.error(err);
        if (err instanceof Error) {
          Toast("Error!!", err.message, "danger");
        } else {
          Toast(
            "Error!!",
            "Something went wrong saving notification.",
            "danger",
          );
        }
      }
      setLoading(false);
    }
  };

  const handleReject = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (
      window.confirm(
        "Are you sure you would like to reject this notification from the send queue?",
      )
    ) {
      onUpdate(false);
    }
  };

  const handleApprove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!count) {
      if (
        !window.confirm(
          "There are no recipients for this notification. Are you sure you would like to approve this notification?",
        )
      ) {
        return;
      }
    }
    onUpdate(true);
  };

  const handleSave = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!loading) {
      setLoading(true);
      try {
        if (!params.uuid) {
          throw new Error("No id for notification provided.");
        }
        if (!recipientsCSVFile) {
          return;
        }
        await notificationService.updateNotificationRecipients(
          params.uuid,
          recipientsCSVFile,
        );
        await init();
        Toast("Success!!", "The recipients have been updated.", "success");
      } catch (err) {
        console.error(err);
        if (err instanceof Error) {
          Toast("Error!!", err.message, "danger");
        } else {
          Toast(
            "Error!!",
            "Something went wrong saving notification.",
            "danger",
          );
        }
      }
      setLoading(false);
    }
  };

  const handleRevoke = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (
      window.confirm(
        "Are you sure you would like to remove this notification from the send queue?",
      )
    ) {
      if (params.uuid) {
        await notificationService.revokeNotification(params.uuid);
        navigate("/approvals");
      }
    }
  };

  const renderActions = () => {
    if (status === "pending") {
      return (
        <div>
          {recipientsCSVFile && (
            <button
              type="button"
              onClick={handleSave}
              className="bg-[#e64626] text-white text-xs rounded px-5 py-2 mr-10"
            >
              Save
            </button>
          )}
          <button
            type="button"
            onClick={handleReject}
            className="bg-red-600 text-white text-xs rounded px-5 py-2 mr-10"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={handleApprove}
            className="bg-[#e64626] text-white text-xs rounded px-5 py-2"
          >
            Approve
          </button>
        </div>
      );
    }
    if (status === "approved") {
      return (
        <button
          type="button"
          onClick={handleRevoke}
          className="bg-red-600 text-white text-xs rounded px-5 py-2"
        >
          Revoke
        </button>
      );
    }
    return <></>;
  };

  const validateCSVForEmails = (file: File) => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          const rows = results.data;
          const containsEmail = rows.some(
            (row: any) =>
              row.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email),
          );
          resolve(containsEmail);
        },
        error: (error) => reject(error),
      });
    });
  };

  const getEmailTemplate = (name: string) => {
    notificationService.getEmailTemplate(name).then((template) => {
      setEmailTemplate(template);
    });
  };

  const handleRecipientsCSVFileChange = async (e: {
    target: HTMLInputElement;
  }) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setRecipientsCSVFile(file);
      const containsEmail = await validateCSVForEmails(file);
      setCsvError(!containsEmail);
    } else {
      setRecipientsCSVFile(undefined);
      setCsvError(false);
    }
  };

  const handleSendTestEmail = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(testEmail)) {
      setEmailError("Invalid email address");
      return;
    }

    setLoading(true);

    try {
      if (!notification || !emailTemplateVariablesMap) {
        throw new Error("notification or template variables are empty");
      }

      await notificationService.sendTestEmail({
        emailTo: testEmail,
        emailSubject:
          // Sample Subject is added for non login email flow
          emailTemplateVariablesMap.get("EmailSubject")! || "Sample Subject",
        emailBody: emailTemplateVariablesMap.get("PersonalMessage")!,
        unitOfStudy: emailTemplateVariablesMap.get("UnitOfStudyCode")!,
        emailTemplate: notification.EmailTemplate,
        emailSignature: emailTemplateVariablesMap.get("EmailSignature")!,
        emailTemplateVariables: {
          // Sample values are added for non login email flow
          UnitOfStudyCode: "Sample UOS",
          UnitOfStudyName: "Sample UOS Name",
          PreferredName: "Sample Preferred Name",
          UOSCName: "Sample Coordinator Name",
          ...notification.EmailTemplateVariables,
        },
        fromEmail: testEmail,
        replyToEmail: testEmail,
        preferredName: "Test Name",
      });

      Toast("Success!!", "Your test email was sent.", "success");
    } catch (err) {
      console.error("sendTestEmail error:", err);

      let message = `Something went wrong. Please try again. ${err}`;
      if (isAxiosError(err)) {
        if (err.response?.data?.message) {
          message = err.response.data.message;
        } else if (err.response?.status === 400) {
          message = "Bad request. Please check the input fields.";
        } else if (err.response?.status === 401) {
          message = "Unauthorized. Please log in again.";
        } else if (err.response?.status === 403) {
          message =
            "The content provided was invalid. Please review and try again.";
        } else if (err.response?.status === 500) {
          message = "Server error. Please try again later.";
        }
      }

      Toast("Error!", message, "danger");
    }

    setLoading(false);

    setShowModal(false);
    setTestEmail("");
  };

  return (
    <Layout userInfo={props.userInfo} loading={loading}>
      <form className="flex flex-col pt-8 px-20">
        <div className="flex flex-row">
          <div className="flex flex-1 flex-col mr-10">
            <div className="flex flex-col pb-4">
              Status <StatusIcon status={status} />
            </div>
            <div className="flex flex-col pb-4">
              <label htmlFor="title" className="text-sm pb-2">
                Notification title - CSV ONLY: supported template keywords{" "}
                <span style={{ fontStyle: "italic" }}>
                  {" "}
                  {`{{unikey}} {{uos}}`}
                </span>
              </label>
              <input
                placeholder="Enter optional title"
                type="text"
                name="title"
                value={title}
                className="border px-2 py-1 text-sm"
                disabled
              />
            </div>
            <div className="flex flex-col pb-4">
              <label htmlFor="body" className="text-sm pb-2">
                Notification text <span className="text-red-500">*</span> - CSV
                ONLY: supported template keywords{" "}
                <span style={{ fontStyle: "italic" }}>
                  {" "}
                  {`{{unikey}} {{uos}}`}
                </span>
              </label>
              <textarea
                placeholder="Enter notification text"
                name="body"
                value={body}
                className="border px-2 py-1 text-sm"
                disabled
              />
            </div>
            <div className="flex flex-col pb-4">
              <label htmlFor="title" className="text-sm pb-2">
                URL redirect upon user interaction (leave blank for no redirect)
              </label>
              <input
                placeholder="eg, http://sydney.edu.au"
                type="url"
                name="link"
                value={link}
                className="border px-2 py-1 text-sm"
                disabled
              />
            </div>
          </div>
          <div className="flex flex-1 flex-col ml-10">
            <div className="flex flex-col pb-4">
              <label htmlFor="sendat" className="text-sm pb-2">
                Schedule date and time <span className="text-red-500">*</span>
              </label>
              <DateTimePicker
                name="sendat"
                value={sendAt}
                inputClassName="border px-2 py-1 text-sm"
                disabled={true}
              />
            </div>
            <p>
              Your notification will be sent at the scheduled date and time
              pending approval by an appropriate administrator.
            </p>
          </div>
        </div>
        <div className="flex flex-row mt-8">
          <div className="flex flex-1 flex-col items-center justify-center">
            <h3>Recipients: {recipientsAll ? "Everyone" : "List"}</h3>
            {status === "pending" && <div>Scheduled Count: {count}</div>}
            {status === "sent" && <div>Delivered Count: {count}</div>}
          </div>
        </div>
        {emailTemplate && (
          <>
            <div className="flex flex-row mt-8 gap-8">
              <div className="flex flex-col flex-1">
                <p>AuthorName: {notification?.AuthorName}</p>
                <p>AuthorUnikey: {notification?.AuthorUnikey}</p>
                <p>FromEmail: {notification?.FromEmail}</p>
                <p>ReplyToEmail: {notification?.ReplyToEmail}</p>
              </div>
              <div className="flex flex-col flex-1">
                <p>Faculty: {emailTemplateVariablesMap?.get("Faculty")}</p>
                <p>
                  UnitOfStudyName:{" "}
                  {emailTemplateVariablesMap?.get("UnitOfStudyName")}
                </p>
                <p>
                  UnitOfStudyCode:{" "}
                  {emailTemplateVariablesMap?.get("UnitOfStudyCode")}
                </p>
                <p>
                  EmailSubject: {emailTemplateVariablesMap?.get("EmailSubject")}
                </p>
              </div>
            </div>
            <div className="flex flex-row mt-10">
              <div className="flex flex-1 flex-col items-center justify-center border border-gray-200">
                <EmailPreview
                  emailTemplate={emailTemplate}
                  emailTemplateVariables={{
                    PreferredName: "Sample Name",
                    UnitOfStudyCode: "Sample UOS",
                    UnitOfStudyName: "Sample UOS Name",
                    UOSCName: "Sample Coordinator Name",
                    ...emailTemplateVariables,
                  }}
                  useEditorStyle={false}
                />
              </div>
            </div>
            <div className="flex items-end my-[14.5px]">
              <button
                type="button"
                className="flex flex-row gap-2 items-center p-2 px-4 bg-white border border-gray-300 rounded-lg h-10 "
                onClick={() => setShowModal(true)}
              >
                <img src="/images/mail.png" alt="mail" className="h-5" />
                Send a Test Email
              </button>
            </div>
          </>
        )}
        <div className="flex flex-col mt-8">
          <label className="text-sm pb-2">
            Recipients (CSV file with heading titled "unikey" and/or "uos").{" "}
            <br />
            Note: This UI has been designed to support approx 20k entries.
          </label>
          <input
            type="file"
            id="fileRecipientsCSV"
            name="fileRecipientsCSV"
            onChange={handleRecipientsCSVFileChange}
            accept=".csv"
          />
          {csvError && (
            <p className="text-red-500 text-xs mt-2">
              CSV file must contain at least one valid email address.
            </p>
          )}
        </div>
        <div className="flex flex-row justify-between items-start pt-8">
          <Link
            to="/approvals"
            className="bg-neutral-700 text-white text-xs rounded px-5 py-2 mr-20"
          >
            Back
          </Link>
          {renderActions()}
        </div>
      </form>
      <Modal show={showModal} onClose={() => setShowModal(false)}>
        <Modal.Header>
          <div className="flex items-center w-full">
            <span className="text-lg font-medium">Send Test Email</span>
            <div className="px-5 ml-auto">
              <Button
                size="xs"
                className="focus:ring-0 focus:outline-none border-none shadow-none"
                onClick={() => setTestEmail(props.userInfo.Email)}
              >
                Send Me
              </Button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <TextInput
            type="email"
            label="Enter Test Email"
            placeholder="Enter email address"
            value={testEmail}
            onChange={(e) => {
              setTestEmail(e.target.value);
              setEmailError("");
            }}
            errorText={emailError}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleSendTestEmail}>Send Test Email</Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showConfirmation} onClose={() => setShowConfirmation(false)}>
        <Modal.Body>
          <div className="text-center text-lg font-semibold">
            Test email sent!
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowConfirmation(false)}>OK</Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default PendingDetailScreen;
